'use client';

import './styles/@theme';
import { useStore } from '@/store/store';
import { classes } from '@/utils/classes/classes';
import { getText } from '@/utils/text/text';
import { useEffect, useMemo, useRef } from 'react';
import { useWinFrequency } from '../../hooks/use-win-frequency';
import { useDynamicFontSize } from '@/app/components/site-wide-jackpot/hooks/use-dynamic-font-size';
import { ValueTicker } from '@/app/elements/value-ticker/value-ticker';
import type { UpdatedJackpotTier } from '@/types/zones/slices/site-wide-jackpot-slice';

type JackpotCardProps = {
    jackpotId: string;
    enableWonXTimesAnimation?: boolean;
    enableJustWonAnimation?: boolean;
};

export const JackpotCard: React.FC<JackpotCardProps> = ({
    jackpotId,
    enableWonXTimesAnimation = false,
    enableJustWonAnimation = false
}) => {
    const { loading, updatedTiers } = useStore((state) => state.siteWideJackpot);
    const { tierWinFrequency } = useWinFrequency(jackpotId);
    const isWinFrequencyActive = useMemo(() => Boolean(tierWinFrequency), [tierWinFrequency]);

    const textRefs = useRef<(HTMLElement | null)[]>([]);
    const { triggerResize } = useDynamicFontSize(textRefs, loading);

    /**
     * Show the won X times animation every 6 seconds
     */
    const wonXTimes = useRef(false);
    useEffect(() => {
        const interval = setInterval(() => (wonXTimes.current = !wonXTimes.current), 6000);
        return () => clearInterval(interval);
    }, []);

    const showSocialProofingWinFrequency = Boolean(
        wonXTimes.current && enableWonXTimesAnimation && isWinFrequencyActive
    );

    const displayConfetti = useRef(false);
    useEffect(() => {
        const timeOuts: ReturnType<typeof setTimeout>[] = [];
        if (updatedTiers.some((tier) => tier.hasJustBeenWon)) {
            timeOuts.push(setTimeout(() => (displayConfetti.current = true), 500));
            timeOuts.push(setTimeout(() => (displayConfetti.current = false), 3500));
        }

        return () => timeOuts.forEach(clearTimeout);
    }, [updatedTiers]);

    const tierJustWon = (tier: UpdatedJackpotTier) => Boolean(enableJustWonAnimation && tier.hasJustBeenWon);

    return (
        <div className="jackpot-card multiplier-dotted-background-fade">
            <div className="inner-card-container">
                {loading ? (
                    <p className="loading-state">{getText({ text: 'components.siteWideJackpots.loadingStatus' })}</p>
                ) : (
                    <div className="inner-card">
                        {updatedTiers.map((tier, index) => (
                            <div key={tier.tier_id} className="card-section">
                                {tierJustWon(tier) && (
                                    <div className="just-won-wrapper" data-testid="just-won-wrapper">
                                        <div className="win-celebrate-wrapper">
                                            {/* Display confetti */}
                                            {displayConfetti && <></>}
                                        </div>
                                        {/* Display just won text with slide in animation */}
                                        <div className="just-won-slide">
                                            <div className="shimmer" />
                                            {getText({ text: 'components.siteWideJackpots.jackpotWon' })}
                                        </div>
                                    </div>
                                )}

                                <img
                                    className={classes(
                                        'card-icon',
                                        showSocialProofingWinFrequency && 'icon-glow',
                                        tierJustWon(tier) && 'icon-glow'
                                    )}
                                    src={tier.logo1?.url}
                                    alt={`${tier.tier_name?.[0]?.text} logo`}
                                />

                                <div className={classes('card-details', tierJustWon(tier) && 'fadeout')}>
                                    <div
                                        className={classes(
                                            'jackpot-value__container glow',
                                            showSocialProofingWinFrequency && 'jackpot-slide'
                                        )}>
                                        <p className="card-title">{tier.tier_name?.[0]?.text}</p>
                                        <div
                                            ref={(el) => {
                                                textRefs.current[index] = el;
                                                triggerResize();
                                            }}
                                            key={tier.tier_id}
                                            className="card-value">
                                            <ValueTicker amount={tier.currentAmount!} prevAmount={tier.prevAmount!} />
                                        </div>
                                    </div>

                                    <div
                                        className={classes(
                                            'jackpot-won-times__container',
                                            showSocialProofingWinFrequency && 'won-times-slide'
                                        )}>
                                        {tierWinFrequency?.[tier.tier_id!] ? (
                                            <div>
                                                {tierWinFrequency[tier.tier_id!] === 1 ? (
                                                    <p className="amount-times-won">
                                                        {getText({
                                                            text: 'components.siteWideJackpots.jackpotWonOneTime'
                                                        })}
                                                    </p>
                                                ) : (
                                                    <p className="amount-times-won">
                                                        {getText({
                                                            text: 'components.siteWideJackpots.timesWon',
                                                            params: [tierWinFrequency[tier.tier_id!]]
                                                        })}
                                                    </p>
                                                )}
                                                <p className="amount-times-won-subtext">
                                                    {getText({ text: 'components.siteWideJackpots.inThePastDay' })}
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="amount-times-won growing-jackpot">
                                                {getText({ text: 'components.siteWideJackpots.jackpotIsGrowing' })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
